/**
 * The DPS-page editable-ephemeral schedule preview (08-PRV §5.1). Mounts the 04-GNT
 * Gantt chart against the capture compute's `preview` rows, with the toolbar subset
 * (search / phase / future-only / zoom + window label / expand-all), the legend, the
 * bulk-shift toolbar, the revert menu, and the stacked-point picker — all reused from the
 * eval dialog. Every edit is a client-only transition through {@link useEphemeralSchedule}:
 * nothing is written anywhere, and the session is cleared on recompute and on leave.
 *
 * Mounts only when `preview.rows` is non-empty; otherwise the capture story's empty states
 * own the space. The capture coverage line drills in here: following the unscheduled count
 * expands the trailing "Unscheduled" group and scrolls the preview into view.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { Button, InlineNotification, Tag } from '@carbon/react';
import { FormattedCount } from 'components/formatted-count';
import { formatNumber } from 'utils/number';
import {
  BulkShiftToolbar,
  RevertMenu,
  ScheduleGantt,
  StackedPointPicker,
  buildRowModel,
  domainRange,
  overlayRowDates,
  replaceWith,
  selectionCount,
  selectionToPoints,
  stackAt,
  stackOpensPicker,
  windowLabel as computeWindowLabel,
  MARKER_COLORS,
  PHASE_COLORS,
  PHASE_LABELS,
  PHASE_ORDER,
  type BulkShiftResult,
  type MilestoneKey,
  type SlotId,
  type StackedMilestone,
} from 'pages/packages/show/eval3-new/schedule-dialog';
import type { SchedulePreviewPayload } from 'types/dps-capture';
import { useCapturePreviewContext } from './capture-preview-context';
import { useEphemeralSchedule } from './use-ephemeral-schedule';
import { SchedulePreviewToolbar } from './schedule-preview-toolbar';
import {
  previewExpandedGroupsAtom,
  previewFutureOnlyAtom,
  previewPhaseFilterAtom,
  previewSearchAtom,
  previewSelectionAtom,
  previewUnscheduledExpandedAtom,
} from './schedule-preview-atoms';
import styles from './schedule-preview.module.scss';

export interface SchedulePreviewProps {
  /** The capture compute's preview payload, or null before the first compute. */
  preview: SchedulePreviewPayload | null;
  /** True while a recompute is in flight — disables new edit gestures (§5.4). */
  loading: boolean;
  /** Bumped when the coverage line's "View unscheduled" is followed (§5.1 drill-in). */
  viewUnscheduledNonce: number;
}

/**
 * Renders the editable-ephemeral schedule preview.
 *
 * @param {SchedulePreviewProps} props - The preview payload, in-flight flag, and the
 *   coverage drill-in signal.
 * @return {JSX.Element | null} The preview, or null when there is nothing to schedule.
 */
export function SchedulePreview({
  preview,
  loading,
  viewUnscheduledNonce,
}: SchedulePreviewProps): JSX.Element | null {
  // Destructured so the callbacks below can depend on the individual (stable) members
  // rather than the `edits` object, whose identity changes every render.
  const {
    workingEntries,
    adjustmentCount,
    applyDrag,
    applyMultiDrag,
    bulkShift,
    revertScope,
    resetAll,
    discardedCount,
    dismissDiscardedNotice,
  } = useEphemeralSchedule(preview);

  const [selection, setSelection] = useAtom(previewSelectionAtom);
  const [expandedGroups, setExpandedGroups] = useAtom(
    previewExpandedGroupsAtom
  );
  const [unscheduledExpanded, setUnscheduledExpanded] = useAtom(
    previewUnscheduledExpandedAtom
  );
  const [futureOnly, setFutureOnly] = useAtom(previewFutureOnlyAtom);
  const [search, setSearch] = useAtom(previewSearchAtom);
  const [phaseFilter, setPhaseFilter] = useAtom(previewPhaseFilterAtom);

  const [bulkResult, setBulkResult] = useState<BulkShiftResult | null>(null);
  const [pickerStack, setPickerStack] = useState<StackedMilestone[]>([]);
  const [pickerSlot, setPickerSlot] = useState<SlotId | null>(null);
  const [windowText, setWindowText] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);
  // The nonce value already acted on — so a remount carrying a stale (non-zero) nonce
  // does not spuriously auto-expand/scroll; only a genuine new request fires the drill-in.
  const lastDrillInNonceRef = useRef(viewUnscheduledNonce);

  // Each fresh mount starts clean: groups collapsed, future-only ON, no selection or
  // filters (the module-scoped atoms otherwise carry a prior visit's transient state) —
  // matching the eval dialog's per-open reset.
  useEffect(() => {
    setSelection(new Set());
    setExpandedGroups(new Set());
    setUnscheduledExpanded(false);
    setFutureOnly(true);
    setSearch('');
    setPhaseFilter(null);
    setBulkResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Coverage drill-in (§5.1): expand the "Unscheduled" group and scroll here — only when
  // the nonce genuinely advances (not on a remount that inherits a stale nonce).
  useEffect(() => {
    if (viewUnscheduledNonce === lastDrillInNonceRef.current) {
      return;
    }
    lastDrillInNonceRef.current = viewUnscheduledNonce;
    setUnscheduledExpanded(true);
    containerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [viewUnscheduledNonce, setUnscheduledExpanded]);

  const rowModel = useMemo(() => {
    if (!preview) {
      return null;
    }
    return buildRowModel(preview.rows, preview.groups, workingEntries, {
      expandedGroupIds: expandedGroups,
      unscheduledExpanded,
      futureOnly,
      effectiveDate: preview.effectiveDate,
      search,
      phaseFilter,
    });
  }, [
    preview,
    workingEntries,
    expandedGroups,
    unscheduledExpanded,
    futureOnly,
    search,
    phaseFilter,
  ]);

  const domain = useMemo(() => {
    if (!preview) {
      return { startMs: 0, endMs: 1 };
    }
    const rowDates = preview.rows.map((row) =>
      overlayRowDates(row, workingEntries)
    );
    return domainRange(rowDates, futureOnly, preview.effectiveDate);
  }, [preview, workingEntries, futureOnly]);

  const defaultWindowText = useMemo(
    () => computeWindowLabel(domain.startMs, domain.endMs),
    [domain.startMs, domain.endMs]
  );

  const handleSelectionChange = useCallback(
    (points: Array<{ slotId: SlotId; field: MilestoneKey }>) => {
      setSelection(replaceWith(points));
    },
    [setSelection]
  );

  // An in-flight recompute freezes edit commits so a gesture cannot race the payload
  // swap (§5.4); selection + filters stay live.
  const handleDragCommit = useCallback(
    (slotId: SlotId, field: MilestoneKey, isoDay: string) => {
      if (loading) {
        return;
      }
      applyDrag(slotId, field, isoDay);
    },
    [loading, applyDrag]
  );

  // Multi-marker drag: grabbing one of ≥2 selected markers shifts the whole selection by
  // the grabbed marker's delta (each other point clamped to its own bounds), in one
  // transform — mirroring the eval dialog. Same recompute freeze as the single drag.
  const handleMultiDrag = useCallback(
    (
      primary: { slotId: SlotId; field: MilestoneKey; iso: string },
      others: Array<{ slotId: SlotId; field: MilestoneKey }>,
      deltaMs: number
    ) => {
      if (loading || !preview) {
        return;
      }
      applyMultiDrag(preview.rows, primary, others, deltaMs);
    },
    [loading, preview, applyMultiDrag]
  );

  const handleStackedClick = useCallback(
    (slotId: SlotId, targetIso: string) => {
      if (loading || !preview) {
        return;
      }
      const row = preview.rows.find((r) => r.slotId === slotId);
      if (!row) {
        return;
      }
      const dates = overlayRowDates(row, workingEntries);
      const stack = stackAt(dates, targetIso);
      if (stackOpensPicker(stack)) {
        setPickerSlot(slotId);
        setPickerStack(stack);
      }
    },
    [loading, preview, workingEntries]
  );

  const handlePick = useCallback(
    (field: MilestoneKey) => {
      if (pickerSlot) {
        setSelection(replaceWith([{ slotId: pickerSlot, field }]));
      }
      setPickerStack([]);
      setPickerSlot(null);
    },
    [pickerSlot, setSelection]
  );

  const handleBulkShift = useCallback(
    (offsetMonths: number) => {
      if (loading || !preview) {
        return;
      }
      const points = selectionToPoints(selection);
      setBulkResult(bulkShift(preview.rows, points, offsetMonths));
    },
    [loading, preview, selection, bulkShift]
  );

  // Per-unit chevron toggle (INS-10671): flip a unit's id in the expanded set, or the
  // "Unscheduled" pseudo-group (groupId null) — the same state the coverage drill-in uses.
  const handleToggleGroup = useCallback(
    (groupId: number | null) => {
      if (groupId === null) {
        setUnscheduledExpanded((expanded) => !expanded);
        return;
      }
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        if (next.has(groupId)) {
          next.delete(groupId);
        } else {
          next.add(groupId);
        }
        return next;
      });
    },
    [setExpandedGroups, setUnscheduledExpanded]
  );

  // Mounts only when there is something to schedule; otherwise the capture empty
  // states own the space (§5.1 / AC 9).
  if (!preview || preview.rows.length === 0 || !rowModel) {
    return null;
  }

  return (
    <div ref={containerRef} className={styles.preview} aria-busy={loading}>
      <div className={styles.header}>
        <p className={styles.subhead}>
          <FormattedCount value={preview.scheduledSlotCount} /> scheduled slots
          across <FormattedCount value={rowModel.groupCount} /> units
        </p>
        <div className={styles.headerActions}>
          {adjustmentCount > 0 && (
            <Tag type="warm-gray" className={styles.adjustmentsChip}>
              <FormattedCount value={adjustmentCount} /> preview adjustment
              {adjustmentCount === 1 ? '' : 's'} — not saved
            </Tag>
          )}
          {adjustmentCount > 0 && (
            <Button
              size="sm"
              kind="ghost"
              onClick={() => {
                resetAll();
                setSelection(new Set());
              }}
            >
              Reset all
            </Button>
          )}
          <RevertMenu
            selection={selection}
            editedDateCount={adjustmentCount}
            onRevert={(scope) => {
              revertScope(scope);
              if (scope.kind === 'all' || scope.kind === 'selection') {
                setSelection(new Set());
              }
            }}
          />
        </div>
      </div>

      {discardedCount !== null && (
        <InlineNotification
          kind="info"
          lowContrast
          title="Preview adjustments discarded"
          subtitle={`${formatNumber({
            value: discardedCount,
            minPrecision: 0,
            includeCommas: true,
          })} preview adjustment${
            discardedCount === 1 ? '' : 's'
          } discarded — the schedule was recomputed.`}
          className={styles.banner}
          onCloseButtonClick={dismissDiscardedNotice}
        />
      )}

      <SchedulePreviewToolbar
        windowLabel={windowText || defaultWindowText}
        shownRowCount={rowModel.shownRowCount}
        totalRowCount={rowModel.totalRowCount}
      />

      <BulkShiftToolbar
        selectedCount={selectionCount(selection)}
        onApply={handleBulkShift}
        onClear={() => setSelection(new Set())}
        lastResult={bulkResult}
      />

      <PreviewLegend />

      <ScheduleGantt
        model={rowModel}
        domain={domain}
        selection={selection}
        order={preview.constraints.order}
        onDragCommit={handleDragCommit}
        onMultiDragCommit={handleMultiDrag}
        onStackedClick={handleStackedClick}
        onSelectionChange={handleSelectionChange}
        onToggleGroup={handleToggleGroup}
        onWindowChange={(startMs, endMs) =>
          setWindowText(computeWindowLabel(startMs, endMs))
        }
      />

      <StackedPointPicker
        open={pickerStack.length > 0}
        stack={pickerStack}
        onPick={handlePick}
        onClose={() => {
          setPickerStack([]);
          setPickerSlot(null);
        }}
      />
    </div>
  );
}

/**
 * Slices a capture preview payload down to a single area's slots for the per-area page
 * view (INS-10671). On the Global page (`areaIndex` -1) the payload is returned UNCHANGED
 * — the same object identity, so the ephemeral edit session is left undisturbed and the
 * chart shows the scenario-wide total exactly as before. For a specific area it keeps
 * only the rows that area won (`areaConfigId`), recomputes the unit groups (name carried
 * from the full payload's groups, count-desc to mirror the backend `unit_groups`
 * ordering) and the scheduled-slot count from the kept rows. `buildRowModel` is left
 * untouched — it still only reads the opaque `groupId`.
 *
 * @param {SchedulePreviewPayload | null} preview - The full capture preview payload.
 * @param {number} areaIndex - The selected area's positional index, or -1 for Global.
 * @return {SchedulePreviewPayload | null} The area-sliced payload (or the input unchanged).
 */
export function sliceScheduleForArea(
  preview: SchedulePreviewPayload | null,
  areaIndex: number
): SchedulePreviewPayload | null {
  if (!preview || areaIndex < 0) {
    return preview;
  }

  const rows = preview.rows.filter((row) => row.areaConfigId === areaIndex);

  const groupNameById = new Map(
    preview.groups.map((group) => [group.id, group.name])
  );
  const slotCountById = new Map<number, number>();
  for (const row of rows) {
    // Mirror the backend `unit_groups`: only rows that render under a unit (scheduled
    // paceable + pdp) count toward a group; unscheduled paceable rows live in the
    // "Unscheduled" pseudo-group regardless of area.
    if (row.unscheduled || row.groupId === null) {
      continue;
    }
    slotCountById.set(row.groupId, (slotCountById.get(row.groupId) ?? 0) + 1);
  }
  const groups = [...slotCountById.entries()]
    .map(([id, slotCount]) => ({
      id,
      name: groupNameById.get(id) ?? String(id),
      slotCount,
    }))
    .sort((a, b) => b.slotCount - a.slotCount);

  const scheduledSlotCount = rows.filter(
    (row) => !row.unscheduled && row.category !== 'pdp'
  ).length;

  return { ...preview, rows, groups, scheduledSlotCount };
}

export interface SchedulePreviewSectionProps {
  /** The selected area's positional index, or -1 for the Global page (INS-10671). */
  areaIndex: number;
}

/**
 * The context-bound mount: reads the shared capture result, slices it to the selected
 * area (or leaves it whole on Global), and drives {@link SchedulePreview} from it. Kept
 * separate so the preview itself stays prop-driven and unit-testable without the provider.
 *
 * @param {SchedulePreviewSectionProps} props - The selected area index.
 * @return {JSX.Element | null} The preview, or null when there is nothing to schedule.
 */
export function SchedulePreviewSection({
  areaIndex,
}: SchedulePreviewSectionProps): JSX.Element | null {
  const { result, loading, viewUnscheduledNonce } = useCapturePreviewContext();
  const fullPreview = result?.preview ?? null;
  // Memoized so the payload identity is stable across renders within one area — the
  // ephemeral edit session keys its "cleared on recompute" behaviour off that identity,
  // so it must change only on a real recompute or an area switch, never every render.
  const preview = useMemo(
    () => sliceScheduleForArea(fullPreview, areaIndex),
    [fullPreview, areaIndex]
  );
  return (
    <SchedulePreview
      preview={preview}
      loading={loading}
      viewUnscheduledNonce={viewUnscheduledNonce}
    />
  );
}

/**
 * The phase-segment + marker-state legend (mirrors the eval dialog's legend).
 *
 * @return {JSX.Element} The legend row.
 */
function PreviewLegend(): JSX.Element {
  return (
    <div className={styles.legend} aria-label="Legend">
      {PHASE_ORDER.map((phase) => (
        <span key={phase} className={styles.legendItem}>
          <span
            className={styles.swatch}
            style={{ backgroundColor: PHASE_COLORS[phase] }}
          />
          {PHASE_LABELS[phase]}
        </span>
      ))}
      <span className={styles.legendDivider} />
      <span className={styles.legendItem}>
        <span
          className={styles.dot}
          style={{ backgroundColor: MARKER_COLORS.onFile }}
        />
        On file
      </span>
      <span className={styles.legendItem}>
        <span
          className={styles.dot}
          style={{ backgroundColor: MARKER_COLORS.scheduled }}
        />
        Scheduled here
      </span>
      <span className={styles.legendItem}>
        <span
          className={styles.dot}
          style={{ backgroundColor: MARKER_COLORS.override }}
        />
        Set by you
      </span>
    </div>
  );
}
